/**
 * @ Author: Housefolios
 * @ Create Time: 2021-09-16 13:21:50
 * @ Modified by: Daniel Clark
 * @ Modified time: 2024-02-02 12:22:38
 * @ Description: Summary of analytics for all filtered properties in the portfolio
 */

import React, { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Grid,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Dialog,
  Divider,
  List,
  ListItemButton,
  Typography,
  Box,
  IconButton,
} from '@mui/material'
import { calcDialogPaperSx } from '@/housefolios-components/PropertyContainer/CenterPage/Common/strategyDashboardSx'
import CloseIcon from '@mui/icons-material/Close'

// Centralized lodash imports - much cleaner!
import { meanBy, sumBy, map } from '@/utils/lodash'
import CountUp from 'react-countup'
import { countUpSafePercent } from './countUpSafePercent'

const CARD_RADIUS = '0.75rem'

const metricTitleSx = {
  fontWeight: 700,
  textTransform: 'uppercase',
  mt: '0.5rem',
  fontSize: '1.15rem !important',
  color: '#070919',
  'html[data-color-mode="dark"] &': {
    color: 'rgba(255, 255, 255, 0.87) !important',
  },
}

const metricCardSx = {
  p: '1rem',
  borderRadius: CARD_RADIUS,
  overflow: 'hidden',
  'html[data-color-mode="dark"] &': {
    color: 'rgba(255, 255, 255, 0.87)',
  },
}

const summaryCardSx = {
  width: '100%',
  borderRadius: CARD_RADIUS,
  overflow: 'visible',
  position: 'relative',
  px: '1.5rem',
  pt: 1,
  pb: 1,
  '& .MuiList-root': {
    px: '0 !important',
  },
  '& .MuiListItemButton-root': {
    pl: '0 !important',
    pr: '0 !important',
  },
}

const summaryCloseBtnSx = {
  flexShrink: 0,
  alignSelf: 'center',
  ml: '4px',
  mr: '-4px',
  zIndex: 6,
  color: 'rgba(0, 0, 0, 0.54)',
  'html[data-color-mode="dark"] &': {
    color: 'rgba(255, 255, 255, 0.75)',
  },
}

const analyticsTabSx = {
  alignItems: 'center',
  alignSelf: 'stretch',
  width: 'auto',
  minHeight: 48,
  bgcolor: 'transparent',
  '&.Mui-selected': {
    bgcolor: 'transparent',
    color: 'primary.main',
  },
  '&.Mui-selected:hover': {
    bgcolor: 'transparent',
  },
  '&:hover': {
    bgcolor: 'transparent',
  },
}

export default function PortfolioAnalyticsComponent(props) {
  const [activeTab, setActiveTab] = useState('0')

  const toggle = (tab) => {
    if (activeTab !== tab) setActiveTab(tab)
  }

  const [modal1, setModal1] = useState(null)

  const toggle1 = (name) => {
    setModal1(name)
  }

  const toggle1Close = () => {
    setModal1(null)
  }

  const { properties, marketplace } = props

  let acquisitions = []
  map(properties, (property) => {
    acquisitions.push(property.acquisition)
  })

  let assumptions = []
  map(properties, (property) => {
    assumptions.push(property.assumptions)
  })

  //VARIABLES FOR ANALYTIC DIALOG BOX
  // Properties
  const length = properties.length

  // Out Of Pocket

  // const test = filter(acquisitions, cor => {
  //   return cor.analysis.financingRepairs === false
  // })

  const totalOutOfPocket =
    sumBy(
      acquisitions,
      marketplace
        ? 'analytics.turnkey.totalOutOfPocketTurnkey'
        : 'analytics.buyHold.totalOutOfPocket',
    ) || 0

  // Total Debt
  const loanAmount =
    sumBy(
      acquisitions,
      marketplace
        ? 'analytics.turnkey.loanAmountTurnkey'
        : 'analytics.buyHold.loanAmount',
    ) || 0

  // All-in Costs
  const allInCost =
    sumBy(
      acquisitions,
      marketplace
        ? 'analytics.turnkey.allInCostTurnkey'
        : 'analytics.buyHold.allInCost',
    ) || 0

  // // Total Value
  // const totalValue =
  //   sumBy(acquisitions, 'analysis.afterRepairValue') || 0

  // // Equity
  // const equity =
  //   sumBy(
  //     acquisitions,
  //     marketplace
  //       ? 'analytics.turnkey.equityTurnkey'
  //       : 'analytics.buyHold.equity',
  //   ) || 0

  const IRR = meanBy(
    acquisitions,
    marketplace
      ? 'analytics.turnkey.IRRTurnkey'
      : 'analytics.buyHold.IRR',
  )

  // Cap Rate
  //    const capRate = meanBy(acquisitions, 'analytics.buyHold.capRate') || 0
  const netOperatingIncome =
    sumBy(
      acquisitions,
      marketplace
        ? 'analytics.turnkey.netOperatingIncomeTurnkey'
        : 'analytics.buyHold.netOperatingIncome',
    ) || 0

  const capRate = netOperatingIncome / allInCost || 0

  // Cash On Cash
  //    const cashOnCash = meanBy(acquisitions, 'analytics.buyHold.cashOnCashReturn') || 0
  const cashFlow =
    sumBy(
      acquisitions,
      marketplace
        ? 'analytics.turnkey.cashFlowTurnkey'
        : 'analytics.buyHold.cashFlow',
    ) || 0

  const cashOnCash = cashFlow / totalOutOfPocket || 0

  // Cash Flow - Defined above as cashFlow

  // Net ROI
  // const netROI = meanBy(acquisitions, 'analytics.buyHold.netROI') || 0
  const proceedsAfterSale =
    sumBy(acquisitions, 'analytics.fixFlip.saleProceeds') || 0

  //const percentageOfARV = allInCost / totalValue || 0

  const netROI = proceedsAfterSale / allInCost || 0

  // Sale Proceeds - Defined above as proceedsAfterSale

  // Calculation Values for Analytic Boxes
  //Properties - Got value above

  // Out Of Pocket
  // downPaymentTotal =
  //   sumBy(acquisitions, 'analytics.buyHold.downPaymentTotal') || 0
  // const acquisitionCommissionDollar =
  //   sumBy(acquisitions, 'analytics.acquisitionCommissionDollar') ||  0
  // const acquisitionFee =
  //   sumBy(assumptions, 'assumptions.acquisitionCommissionDollar') || 0
  // const closeCostDollar =
  //   sumBy(acquisitions, 'analytics.buyHold.closeCostDollar') || 0
  // const outOfPocketCostOfRepairs = sumBy(test, 'analytics.buyHold.cor') || 0
  //Based on conditional of if Finance Repair is Y
  // const financingFeeTotal =
  //   sumBy(acquisitions, 'analytics.buyHold.financingFeeTotal') || 0
  ////////////////////////////////////////////////
  // const pointsAmount =
  //   sumBy(acquisitions, 'analytics.buyHold.pointsAmount') || 0

  // Total Debt
  // const totalBeforeDownPayment =
  //   sumBy(acquisitions, 'analytics.buyHold.totalBeforeDownPayment') || 0
  //downPaymentTotal defined above

  // All-in Costs
  // const offerPrice =
  //   sumBy(
  //     acquisitions,
  //     marketplace ? 'analysis.sellPrice' : 'analytics.buyHold.sop',
  //   ) || 0
  // const allInCostCostOfRepairs =
  //   sumBy(acquisitions, 'analytics.buyHold.corTotal') || 0
  //acquisitionCommissionDollar defined above
  //acquisitionFee defined above
  //closeCostDollar defined above
  //pointsAmount defined above

  // Total Value - Got Value above

  // Equity
  //afterRepairValue defined above as totalValue
  //allInCost defined above

  // Gross Yield
  //potentialRent defined above
  //allInCost defined above

  // Cap Rate
  //netOperatingIncome is defined above
  //allInCost defined above

  // Cash on Cash
  //cashFlow defined above
  //totalOutOfPocket defined above

  // Cash Flow (yr)
  //netOperatingIncome is defined above
  // const annualMortgage =
  //   sumBy(acquisitions, 'analytics.buyHold.mp_monthly') * 12 || 0

  // Net ROI
  // const grossAnnualRent =
  //   sumBy(acquisitions, 'analytics.grossAnnualRent') || 0
  // const annualExpenses =
  //   sumBy(acquisitions, 'analytics.buyHold.annualExpenses') || 0

  // Sale Proceeds
  // const salePrice = sumBy(acquisitions, 'analytics.buyHold.salePrice') || 0
  // const salesCommissionTotal =
  //   sumBy(acquisitions, 'analytics.buyHold.salesCommissionTotal') || 0
  // const sellingClosingCostDollar =
  //   sumBy(acquisitions, 'analytics.buyHold.sellingClosingCostDollar') || 0
  // const dispositionFeeDollar =
  //   sumBy(acquisitions, 'analytics.buyHold.dispositionFeeDollar') || 0
  // //totalOutOfPocket defined above
  // const loanBalanceAtHoldingPeriod =
  //   sumBy(acquisitions, 'analytics.buyHold.loanBalanceAtHoldingPeriod') || 0

  //REPLACE totalCashIn with totalOutofPocket
  // const totalCashIn =
  //   sumBy(acquisitions, function(tci) {
  //     return (
  //       tci.analytics.buyHold.allInCost -
  //       tci.analytics.buyHold.loanAmount +
  //       tci.analytics.buyHold.pointsAmount +
  //       tci.analytics.buyHold.financingFeeTotal
  //     )
  //   }) || 0

  // REPLACE netYield with capRate
  // const netYield =
  //   meanBy(acquisitions, function(ny) {
  //     return (
  //       ny.analytics.buyHold.cashFlow /
  //       (ny.analytics.buyHold.allInCost -
  //         ny.analytics.buyHold.loanAmount +
  //         ny.analytics.buyHold.pointsAmount +
  //         ny.analytics.buyHold.financingFeeTotal)
  //     )
  //   }) || 0

  const analytics = [
    {
      label: 'Net Cashflow',
      description: `Net cashflow is the amount of money left after the mortgage and any other expenses are paid.`,
      calculation: (
        <>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                Gross Annual Income
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Property Mgmt
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Maintenance
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Insurance
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Taxes
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -HOA Fees
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Asset management
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Utilities
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Capex
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Other
              </Typography>
            </Grid>
          </Grid>
          <Divider style={{ backgroundColor: '#9198a8' }} />
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: 'white' }}>
                Net Operating Income
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Annual Mortgage
              </Typography>
            </Grid>
          </Grid>
          <Divider style={{ backgroundColor: '#9198a8' }} />
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: 'white' }}>
                Net Annual Cashflow
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                <FontAwesomeIcon
                  icon={['fal', 'divide']}
                  style={{ marginRight: 3 }}
                />
                12 months
              </Typography>
            </Grid>
          </Grid>
          <Divider style={{ backgroundColor: '#9198a8' }} />
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: 'white' }}>
                Net Monthly Cashflow
              </Typography>
            </Grid>
          </Grid>
        </>
      ),
    },
    {
      label: 'CoC ROI',
      description: `A cash-on-cash return calculates the return on the money put in by the investor on a property with a mortgage. Commonly referred to as a Leveraged Return.`,
      calculation: (
        <>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                Gross Annual Income
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Property Mgmt
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Maintenance
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Insurance
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Taxes
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -HOA Fees
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Asset management
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Utilities
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Capex
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Other
              </Typography>
            </Grid>
          </Grid>
          <Divider style={{ backgroundColor: '#9198a8' }} />
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: 'white' }}>
                Net Operating Income
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Annual Mortgage
              </Typography>
            </Grid>
          </Grid>
          <Divider style={{ backgroundColor: '#9198a8' }} />
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: 'white' }}>
                Net Annual Cashflow
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                <FontAwesomeIcon
                  icon={['fal', 'divide']}
                  style={{ marginRight: 3 }}
                />
                Total Out-of-Pocket
              </Typography>
            </Grid>
          </Grid>
          <Divider style={{ backgroundColor: '#9198a8' }} />
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: 'white' }}>
                Cash on Cash Return
              </Typography>
            </Grid>
          </Grid>
        </>
      ),
    },
    {
      label: 'IRR',
      description: `IRR measures an investment’s annual return throughout the entire property ownership time frame. Unlike a cash-on-cash return or cap rate that measures returns in annual segments, IRR weighs total earnings from the day you purchase your property to the day you sell it.`,
      calculation: (
        <>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography
                style={{ color: '#9198a8', marginBottom: 20 }}
              >
                Due to the complexity of this calculation, we only
                show an overview of what goes into calculating the IRR
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                Initial Investment (Out of pocket)
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                Gross Monthly Income
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                Rent Growth Rate
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                Monthly Expenses
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                Expenses Growth Rate
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                After Repair Value
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                Appreciation Rate
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                Holding Period (years)
              </Typography>
            </Grid>
          </Grid>
          <Divider style={{ backgroundColor: '#9198a8' }} />
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: 'white' }}>
                Future property sale
              </Typography>
            </Grid>
          </Grid>
          <Divider style={{ backgroundColor: '#9198a8' }} />
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: 'white' }}>IRR</Typography>
            </Grid>
          </Grid>
        </>
      ),
    },
    {
      label: 'Yield-on-Cost',
      description: `The Yield-on-Cost is the rate of return an investor can expect from an investment in real estate, assuming they pay all cash.`,
      calculation: (
        <>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                Gross Annual Income
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Property Mgmt
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Maintenance
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Insurance
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Taxes
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -HOA Fees
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Asset management
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Utilities
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Capex
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Other
              </Typography>
            </Grid>
          </Grid>
          <Divider style={{ backgroundColor: '#9198a8' }} />
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: 'white' }}>
                Net Operating Income
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                <FontAwesomeIcon
                  icon={['fal', 'divide']}
                  style={{ marginRight: 3 }}
                />
                All-in-cost
              </Typography>
            </Grid>
          </Grid>
          <Divider style={{ backgroundColor: '#9198a8' }} />
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: 'white' }}>
                Yield-on-Cost
              </Typography>
            </Grid>
          </Grid>
        </>
      ),
    },
    {
      label: 'Net Profit',
      description:
        'The amount of money left over from selling your home after accounting for the remaining mortgage balance, closing costs, and realtor fees.',
      calculation: (
        <>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                Resale Price
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Sales Commission
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Disposititon Fee
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Total Out-of-Pocket
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Remaining Loan Balance
              </Typography>
            </Grid>
          </Grid>
          <Divider style={{ backgroundColor: '#9198a8' }} />
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: 'white' }}>
                Net Profit
              </Typography>
            </Grid>
          </Grid>
        </>
      ),
    },
    {
      label: 'Net ROI',
      description:
        'Return on investment (ROI) indicates the percentage of investment money that is recouped after the deduction of associated costs.',
      calculation: (
        <>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                Resale Price
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Sales Commission
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Disposititon Fee
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Total Out-of-Pocket
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -Remaininig Loan Balance
              </Typography>
            </Grid>
          </Grid>
          <Divider style={{ backgroundColor: '#9198a8' }} />
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: 'white' }}>
                Net Profit
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                <FontAwesomeIcon
                  icon={['fal', 'divide']}
                  style={{ marginRight: 3 }}
                />
                All-in-cost
              </Typography>
            </Grid>
          </Grid>
          <Divider style={{ backgroundColor: '#9198a8' }} />
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: 'white' }}>
                Net ROI
              </Typography>
            </Grid>
          </Grid>
        </>
      ),
    },
    {
      label: 'Equity',
      description:
        'Equity is the property’s current market value, less any liens that are attached to that property.',
      calculation: (
        <>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                After Repair Value
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                -All-in-cost
              </Typography>
            </Grid>
          </Grid>
          <Divider style={{ backgroundColor: '#9198a8' }} />
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: 'white' }}>
                Equity
              </Typography>
            </Grid>
          </Grid>
        </>
      ),
    },
    {
      label: 'After Repair Value',
      description:
        'The ARV is the value of a property after you have conducted repairs and are ready to sell.',
      calculation: (
        <>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                The sum of After Repair Values
              </Typography>
            </Grid>
          </Grid>
        </>
      ),
    },
    {
      label: 'All-In Cost to ARV',
      description: `The All-In Cost to ARV (After Repair Value) that you purchase the property for. There is a popular term referred to as the “70% rule” when applied to fix and flipping houses.  The 70% rule states that an investor should pay 70 percent of the ARV of a property, minus the repairs needed.  The ARV is the after repaired value and is what a home is worth after it is fully repaired.  This “rule” says 70% but we have still found success at 80% of the ARV.`,
      calculation: (
        <>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                Purchase Price
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                +Acquisition Commission
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                +Acquisition Fee
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                +Closing Costs
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                +Loan Points
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                +Rehab Amount
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                +Custom Acquisition Expenses
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                +Holding Expenses
              </Typography>
            </Grid>
          </Grid>
          <Divider style={{ backgroundColor: '#9198a8' }} />
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: 'white' }}>
                All-in Cost
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: '#9198a8' }}>
                <FontAwesomeIcon
                  icon={['fal', 'divide']}
                  style={{ marginRight: 3 }}
                />
                After Repair Value
              </Typography>
            </Grid>
          </Grid>
          <Divider style={{ backgroundColor: '#9198a8' }} />
          <Grid
            container
            style={{ marginTop: 5, marginBottom: 5 }}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography style={{ color: 'white' }}>
                All-in Cost to ARV
              </Typography>
            </Grid>
          </Grid>
        </>
      ),
    },
  ]

  // const analyticCardsCol1 = [
  //   {
  //     name: 'Properties',
  //     icon: <FontAwesomeIcon
    //             icon={['fas', 'house-chimney']}
    //
    //           />,
    //     className: 'd-60 rounded border-0 card-icon-wrapper flex-shrink-0 bg-first text-white btn-icon text-center shadow-first mr-3',
    //     value: length,
    //     prefix: '',
    //     definition: 'Number of Properties contributing to calculations',
    //   },
    //   {
    //     divider: <div />,
    //     // name: 'Out-of-Pocket',
    //     name: 'Initial Investment',
    //     icon: <FontAwesomeIcon
    //             icon={['fas', 'wallet']}
    //
    //           />,
    //     className: 'd-60 rounded border-0 card-icon-wrapper flex-shrink-0 bg-success text-white btn-icon text-center shadow-success mr-3',
    //     value: totalOutOfPocket,
    //     prefix: '$',
    //     definition: 'Total loan amount needed to purchase the properties',
    //   },
    //   {
    //     divider: <div />,
    //     name: 'Total Debt',
    //     icon: <FontAwesomeIcon
    //             icon={['fas', 'building-columns']}
    //
    //           />,
    //     className: 'd-60 rounded border-0 card-icon-wrapper flex-shrink-0 bg-danger text-white btn-icon text-center shadow-danger mr-3',
    //     value: loanAmount,
    //     prefix: '$',
    //     definition: 'Total loan amount needed to purchase the properties',
    //   },
    //   {
    //     divider: <div />,
    //     name: 'All-in Costs',
    //     icon: <FontAwesomeIcon
    //             icon={['fas', 'money-check-dollar']}
    //
    //           />,
    //     className: 'd-60 rounded border-0 card-icon-wrapper flex-shrink-0 bg-warning text-white btn-icon text-center shadow-warning mr-3',
    //     value: allInCost,
    //     prefix: '$',
    //     definition: 'Sum of total costs associated with properties',
    //   },
    // ]
    // const analyticCardsCol2 = [
    //   {
    //     name: 'Total Portfolio Value',
    //     icon: <FontAwesomeIcon
    //             icon={['fas', 'money-check-dollar']}
    //
    //           />,
    //     className: 'd-60 rounded border-0 card-icon-wrapper flex-shrink-0 bg-warning text-white btn-icon text-center shadow-warning mr-3',
    //     value: '3070500',
    //     prefix: '$',
    //     definition: 'Sum of total costs associated with properties',
    //   },
    //   {
    //     divider: <div />,
    //     name: 'Equity Value',
    //     icon: <FontAwesomeIcon
    //             icon={['fas', 'money-check-dollar']}
    //
    //           />,
    //     className: 'd-60 rounded border-0 card-icon-wrapper flex-shrink-0 bg-warning text-white btn-icon text-center shadow-warning mr-3',
    //     value: '584227',
    //     prefix: '$',
    //     definition: 'Sum of total costs associated with properties',
    //   },
    //   {
    //     divider: <div />,
    //     name: 'Current Reserves',
    //     icon: <FontAwesomeIcon
    //             icon={['fas', 'money-check-dollar']}
    //
    //           />,
    //     className: 'd-60 rounded border-0 card-icon-wrapper flex-shrink-0 bg-warning text-white btn-icon text-center shadow-warning mr-3',
    //     value: '78525',
    //     prefix: '$',
    //     definition: 'Sum of total costs associated with properties',
    //   },
    //   {
    //     divider: <div />,
    //     // name: Initial Investment',
    //     name: 'Total Cashflow to Date',
    //     icon: <FontAwesomeIcon
    //             icon={['fas', 'money-check-dollar']}
    //
    //           />,
    //     className: 'd-60 rounded border-0 card-icon-wrapper flex-shrink-0 bg-warning text-white btn-icon text-center shadow-warning mr-3',
    //     value: '2486273',
    //     prefix: '$',
    //     definition: 'Sum of total costs associated with properties',
    //   },
    // ]
    // const analyticCardsCol3 = [
    //   {
    //     name: 'Projected Gross Income',
    //     icon: <FontAwesomeIcon
    //             icon={['fas', 'money-check-dollar']}
    //
    //           />,
    //     className: 'd-60 rounded border-0 card-icon-wrapper flex-shrink-0 bg-warning text-white btn-icon text-center shadow-warning mr-3',
    //     value: '355818',
    //     prefix: '$',
    //     definition: 'Projected 12 month income',
    //   },
    //   {
    //     divider: <div />,
    //     name: 'Projected Expenses',
    //     icon: <FontAwesomeIcon
    //             icon={['fas', 'money-check-dollar']}
    //
    //           />,
    //     className: 'd-60 rounded border-0 card-icon-wrapper flex-shrink-0 bg-warning text-white btn-icon text-center shadow-warning mr-3',
    //     value: '149164',
    //     prefix: '$',
    //     definition: 'Sum of total costs associated with properties',
    //   },
    //   {
    //     divider: <div />,
    //     name: 'Projected Net income',
    //     icon: <FontAwesomeIcon
    //             icon={['fas', 'money-check-dollar']}
    //
    //           />,
    //     className: 'd-60 rounded border-0 card-icon-wrapper flex-shrink-0 bg-warning text-white btn-icon text-center shadow-warning mr-3',
    //     value: '163910',
    //     prefix: '$',
    //     definition: 'Sum of total costs associated with properties',
    //   },
    //   // {
    //   //   divider: <div />,
    //   //   name: 'Avg. YTD Collection Rate',
    //   //   icon: <FontAwesomeIcon
    //   //           icon={['fas', 'money-check-dollar']}
    //   //
    //   //         />,
    //   //   className: 'd-60 rounded border-0 card-icon-wrapper flex-shrink-0 bg-warning text-white btn-icon text-center shadow-warning mr-3',
    //   //   value: '90',
    //   //   prefix: '',
    //   //   suffix: '%',
    //   //   definition: 'Sum of total costs associated with properties',
    //   // },
    //   {
    //     divider: <div />,
    //     name: 'Current Vacancy Rate',
    //     icon: <FontAwesomeIcon
    //             icon={['fas', 'money-check-dollar']}
    //
    //           />,
    //     className: 'd-60 rounded border-0 card-icon-wrapper flex-shrink-0 bg-warning text-white btn-icon text-center shadow-warning mr-3',
    //     value: '0.00',
    //     prefix: '',
    //     suffix: '%',
    //     definition: 'Sum of total costs associated with properties',
    //   },
    // ]
    return (
    <>
      <Box sx={{ marginBottom: "24px" }}>
        <Grid container spacing={3} sx={{ rowGap: 3, columnGap: 3 }}>
          <Grid size={{ lg: 7 }}>
            <Grid container spacing={3} sx={{ rowGap: 3, columnGap: 3 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Card variant="outlined" sx={metricCardSx}>
                  <Box sx={metricTitleSx}>
                    Properties
                  </Box>
                  <Box sx={{ my: '0.5rem' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        py: '1.5rem',
                        alignItems: 'center',
                      }}
                    >
                      <Box sx={{
                        height: "60px !important",
                        width: "60px !important",
                        lineHeight: "60px !important",
                        display: "inline-flex !important",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 0,
                        border: 0,
                        borderRadius: "0.65rem",
                        flexShrink: 0,
                        color: "#fff",
                        textAlign: "center",
                        mr: "1rem",
                        boxShadow: "0 0.313rem 0.8rem rgba(65, 145, 255, 0.5), 0 0.126rem 0.225rem rgba(65, 145, 255, 0.3) !important",
                      }} style={{ backgroundColor: '#4191ff' }}>
                        <FontAwesomeIcon icon={['fas', 'house-chimney']} sx={{ fontSize: "1.7rem", fontWeight: "300", lineHeight: "1.2" }} />
                      </Box>
                      <Box sx={[{ fontWeight: 700, ml: '0.25rem' }, { fontSize: "2.5rem", fontWeight: "300", lineHeight: "1.2" }]}>
                        <Box component="span" sx={{ pr: '0.25rem' }}>
                          <CountUp
                            start={0}
                            end={length}
                            duration={1}
                            separator=","
                            delay={1}
                            decimal="."
                            decimals={0}
                            prefix=""
                            suffix=""
                          />
                        </Box>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        color: 'rgba(7, 9, 25, 0.5)',
                        fontWeight: 700,
                        mb: '0.5rem',
                        'html[data-color-mode="dark"] &': {
                          color: 'rgba(255, 255, 255, 0.68) !important',
                        },
                      }}
                    >
                      Total number of properties that are contributing
                      to calculations.
                    </Box>
                  </Box>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Card variant="outlined" sx={metricCardSx}>
                  <Box sx={metricTitleSx}>
                    Out-of-Pocket
                  </Box>
                  <Box sx={{ my: '0.5rem' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        py: '1.5rem',
                        alignItems: 'center',
                      }}
                    >
                      <Box sx={{
                        height: "60px !important",
                        width: "60px !important",
                        lineHeight: "60px !important",
                        display: "inline-flex !important",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 0,
                        border: 0,
                        borderRadius: "0.65rem",
                        flexShrink: 0,
                        color: "#fff",
                        textAlign: "center",
                        mr: "1rem",
                        backgroundColor: "#1bc943",
                        boxShadow: "0 0.313rem 0.8rem rgba(27, 201, 67, 0.5), 0 0.126rem 0.225rem rgba(27, 201, 67, 0.3) !important",
                      }}>
                        <FontAwesomeIcon icon={['fas', 'wallet']} sx={{ fontSize: "1.7rem", fontWeight: "300", lineHeight: "1.2" }} />
                      </Box>
                      <Box sx={[{ fontWeight: 700, ml: '0.25rem' }, { fontSize: "2.5rem", fontWeight: "300", lineHeight: "1.2" }]}>
                        <Box component="span" sx={{ pr: '0.25rem' }}>
                          <CountUp
                            start={0}
                            end={totalOutOfPocket}
                            duration={1}
                            separator=","
                            delay={1}
                            decimals={0}
                            decimal="."
                            prefix="$"
                            suffix=""
                          />
                        </Box>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        color: 'rgba(7, 9, 25, 0.5)',
                        fontWeight: 700,
                        mb: '0.5rem',
                        'html[data-color-mode="dark"] &': {
                          color: 'rgba(255, 255, 255, 0.68) !important',
                        },
                      }}
                    >
                      Amount needed for downpayment, closing costs,
                      and rehab.
                    </Box>
                  </Box>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Card variant="outlined" sx={metricCardSx}>
                  <Box sx={metricTitleSx}>
                    Total Debt
                  </Box>
                  <Box sx={{ my: '0.5rem' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        py: '1.5rem',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                      }}
                    >
                      <Box sx={{
                        height: "60px !important",
                        width: "60px !important",
                        lineHeight: "60px !important",
                        display: "inline-flex !important",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 0,
                        border: 0,
                        borderRadius: "0.65rem",
                        flexShrink: 0,
                        color: "#fff",
                        textAlign: "center",
                        mr: "1rem",
                        backgroundColor: "#f83245",
                        boxShadow: "0 0.313rem 0.8rem rgba(248, 50, 69, 0.5), 0 0.126rem 0.225rem rgba(248, 50, 69, 0.3) !important",
                      }}>
                        <FontAwesomeIcon icon={['fas', 'building-columns']} sx={{ fontSize: "1.7rem", fontWeight: "300", lineHeight: "1.2" }} />
                      </Box>
                      <Box sx={[{ fontWeight: 700, ml: '0.25rem' }, { fontSize: "2.5rem", fontWeight: "300", lineHeight: "1.2" }]}>
                        <Box component="span" sx={{ pr: '0.25rem' }}>
                          <CountUp
                            start={0}
                            end={loanAmount}
                            duration={1}
                            separator=","
                            delay={1}
                            decimals={0}
                            decimal="."
                            prefix="$"
                            suffix=""
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      color: 'rgba(7, 9, 25, 0.5)',
                      fontWeight: 700,
                      mb: '0.5rem',
                      'html[data-color-mode="dark"] &': {
                        color: 'rgba(255, 255, 255, 0.68) !important',
                      },
                    }}
                  >
                    Total loan amount needed to purchase the
                    properties
                  </Box>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Card variant="outlined" sx={metricCardSx}>
                  <Box sx={metricTitleSx}>
                    All-in Costs
                  </Box>
                  <Box sx={{ my: '0.5rem' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        py: '1.5rem',
                        alignItems: 'center',
                      }}
                    >
                      <Box sx={{
                        height: "60px !important",
                        width: "60px !important",
                        lineHeight: "60px !important",
                        display: "inline-flex !important",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 0,
                        border: 0,
                        borderRadius: "0.65rem",
                        flexShrink: 0,
                        color: "#fff",
                        textAlign: "center",
                        mr: "1rem",
                        backgroundColor: "#f4772e",
                        boxShadow: "0 0.313rem 0.8rem rgba(244, 119, 46, 0.5), 0 0.126rem 0.225rem rgba(244, 119, 46, 0.3) !important",
                      }}>
                        <FontAwesomeIcon icon={['fas', 'money-check-dollar']} sx={{ fontSize: "1.7rem", fontWeight: "300", lineHeight: "1.2" }} />
                      </Box>
                      <Box sx={[{ fontWeight: 700, ml: '0.25rem' }, { fontSize: "2.5rem", fontWeight: "300", lineHeight: "1.2" }]}>
                        <Box component="span" sx={{ pr: '0.25rem' }}>
                          <CountUp
                            start={0}
                            end={allInCost}
                            duration={1}
                            separator=","
                            delay={1}
                            decimals={0}
                            decimal="."
                            prefix="$"
                            suffix=""
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      color: 'rgba(7, 9, 25, 0.5)',
                      fontWeight: 700,
                      mb: '0.5rem',
                      'html[data-color-mode="dark"] &': {
                        color: 'rgba(255, 255, 255, 0.68) !important',
                      },
                    }}
                  >
                    Sum of total costs associated with properties
                  </Box>
                </Card>
              </Grid>
            </Grid>
          </Grid>
          <Grid size={{ lg: 5 }} sx={{ display: 'flex' }}>
            <Card variant="outlined" sx={summaryCardSx}>
              <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
              <List component="div" disablePadding sx={[{
                paddingTop: 0,
                paddingBottom: 0,
                "& .MuiListItem-root.MuiListItem-button, & .MuiListItemButton-root, & > .MuiListItem-root": {
                  color: "#a6a6b9",
                  position: "relative",
                  transition: "all 0.2s ease-in-out",
                  padding: "0.75rem 1.25rem 1rem",
                  background: "transparent",
                  width: "auto",
                  minHeight: 48,
                  margin: 0,
                  borderRadius: 0,
                  alignItems: "center",
                  alignSelf: "stretch",
                  boxSizing: "border-box",
                  "& .divider": {
                    width: "100%",
                    borderRadius: "0.75rem",
                    height: 3,
                    background: "#368cff",
                    visibility: "hidden",
                    opacity: 0,
                    transform: "translateY(-10px)",
                    transition: "all 0.2s ease-in-out",
                    position: "absolute",
                    left: 0,
                    bottom: 0,
                  },
                  "&:hover": { color: "#000", background: "transparent" },
                  "&.Mui-selected": {
                    color: "#368cff",
                    background: "transparent",
                    "&:hover": { color: "#368cff" },
                    "& .divider": { visibility: "inherit", opacity: 1, transform: "translateY(0)" },
                    fontWeight: 700,
                  },
                },
                position: "relative",
                zIndex: 4,
                padding: 0,
                "&::after": {
                  background: "#f4f5fd",
                  height: 3,
                  borderRadius: "0.75rem",
                  display: "block",
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  width: "100%",
                  content: "\"\"",
                  zIndex: 5,
                },
                "& .MuiListItem-root.MuiListItem-button + .MuiListItem-root.MuiListItem-button, & .MuiListItemButton-root + .MuiListItemButton-root": { marginLeft: "2rem" },
                "& .MuiListItem-root.MuiListItem-button, & .MuiListItemButton-root": {
                  padding: "0.75rem 0 1rem",
                  margin: 0,
                  "& .divider": { zIndex: 8, left: 0, width: "100%" },
                },
                "& .MuiListItem-root.MuiListItem-button.Mui-selected": {
                  color: "#368cff",
                  borderTop: "#368cff solid 3px",
                  "&:hover": { color: "#368cff" },
                },
                "& .MuiListItem-root.MuiListItem-button .divider, & .MuiListItemButton-root .divider": { background: "#368cff" },
                "& .MuiListItem-root.MuiListItem-button.Mui-selected, & .MuiListItemButton-root.Mui-selected": {
                  color: "#368cff",
                  "&:hover": { color: "#368cff" },
                },
              }, { display: 'flex', flex: 1, minWidth: 0 }]}>
                <ListItemButton
                  disableRipple
                  selected={activeTab === '0'}
                  onClick={() => {
                    toggle('0')
                  }}
                  sx={analyticsTabSx}
                >
                  <span style={{ fontSize: 20 }}>
                    Cashflow Summary
                  </span>
                  <Box sx={{ background: "#eeeff8", height: "1px", overflow: "hidden" }} />
                </ListItemButton>
                <ListItemButton
                  disableRipple
                  selected={activeTab === '1'}
                  onClick={() => {
                    toggle('1')
                  }}
                  sx={analyticsTabSx}
                >
                  <span style={{ fontSize: 20 }}>Equity Summary</span>
                  <Box sx={{ background: "#eeeff8", height: "1px", overflow: "hidden" }} />
                </ListItemButton>
                <ListItemButton
                  disableRipple
                  selected={activeTab === '2'}
                  onClick={() => {
                    toggle('2')
                  }}
                  sx={analyticsTabSx}
                >
                  <span style={{ fontSize: 20 }}>Year-to-Date</span>
                  <Box sx={{ background: "#eeeff8", height: "1px", overflow: "hidden" }} />
                </ListItemButton>
              </List>
              {props.onClose && (
                <IconButton
                  aria-label="Close analytics"
                  onClick={props.onClose}
                  size="small"
                  sx={summaryCloseBtnSx}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
              </Box>
              <Divider />
              {activeTab === '0' && (
                <List component="div" sx={[{
                  paddingTop: 0,
                  paddingBottom: 0,
                  "& .MuiListItem-root, & .MuiListItemButton-root": {
                    width: "100%",
                    padding: "0.75rem 1.25rem",
                    backgroundColor: "#fff",
                    border: "1px solid rgba(0, 0, 0, 0.125)",
                    borderRadius: 0,
                    margin: 0,
                    borderWidth: "0 0 1px",
                    "&.MuiListItem-button": {
                      borderRadius: 0,
                      borderWidth: "0 0 1px",
                      "& .MuiTouchRipple-root": { opacity: 0.2 },
                    },
                    "&.list-group-item-action": { transition: "all 0.2s ease-in-out" },
                    "&.MuiListItem-button:hover, &.list-group-item-action:hover": { background: "#f4f5fd", zIndex: 1 },
                    "&.MuiListItem-button:active, &.list-group-item-action:active": { color: "#000", backgroundColor: "#eeeff8" },
                    "&:first-of-type": { borderTop: "0 !important" },
                    "&:last-child": { borderBottom: "0 !important" },
                  },
                }, { pb: 1 }]}>
                  <ListItemButton
                    component="a"
                    href={null}
                    onClick={() => toggle1('IRR')}
                    sx={{
                      transition: "all 0.2s ease-in-out !important",
                      transform: "scale(1) !important",
                      boxShadow: "none",
                      position: "relative",
                      zIndex: "0",
                      "&:hover": { borderRadius: "0.65rem", transform: "scale(1.08)", zIndex: 3 },
                      display: "flex",
                      backgroundColor: "#fff",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: "1.5rem",
                    }}
                    >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      <div>
                        <Box sx={[{ fontWeight: 700 }, {
                          fontSize: "1.15rem !important",
                          color: "#070919",
                          "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
                        }]}>
                          IRR
                        </Box>
                        <Box sx={[{ display: 'flex', alignItems: 'center', }, { fontSize: "1rem !important" }]}>
                          <Box sx={{
                            color: "#070919",
                            "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
                          }}>
                            Annualized rate of return.
                          </Box>
                        </Box>
                      </div>
                    </Box>
                    <div>
                      <Box component="span" sx={{ pr: '0.25rem' }}>
                        <CountUp
                          start={0}
                          end={countUpSafePercent(IRR * 100)}
                          duration={1}
                          separator=","
                          delay={1}
                          decimals={2}
                          decimal="."
                          prefix=""
                          suffix="%"
                          style={{ fontSize: 24 }}
                        />
                      </Box>
                    </div>
                  </ListItemButton>
                  <ListItemButton
                    component="a"
                    href={null}
                    onClick={() => toggle1('Yield-on-Cost')}
                    sx={{
                      transition: "all 0.2s ease-in-out !important",
                      transform: "scale(1) !important",
                      boxShadow: "none",
                      position: "relative",
                      zIndex: "0",
                      "&:hover": { borderRadius: "0.65rem", transform: "scale(1.08)", zIndex: 3 },
                      display: "flex",
                      backgroundColor: "#fff",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: "1.5rem",
                    }}
                    >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      <div>
                        <Box sx={[{ fontWeight: 700 }, {
                          fontSize: "1.15rem !important",
                          color: "#070919",
                          "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
                        }]}>
                          {/* Yield on Cost */}
                          Cap Rate on Cost
                        </Box>
                        <Box sx={[{ display: 'flex', alignItems: 'center', }, { fontSize: "1rem !important" }]}>
                          <Box sx={{
                            color: "#070919",
                            "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
                          }}>
                            Yield on Cost for portfolio.
                          </Box>
                        </Box>
                      </div>
                    </Box>
                    <div>
                      <Box component="span" sx={{ pr: '0.25rem' }}>
                        <CountUp
                          start={0}
                          end={countUpSafePercent(capRate * 100)}
                          duration={1}
                          separator=","
                          delay={1}
                          decimals={2}
                          decimal="."
                          prefix=""
                          suffix="%"
                          style={{ fontSize: 24 }}
                        />
                      </Box>
                    </div>
                  </ListItemButton>
                  <ListItemButton
                    component="a"
                    href={null}
                    onClick={() => toggle1('CoC ROI')}
                    sx={{
                      transition: "all 0.2s ease-in-out !important",
                      transform: "scale(1) !important",
                      boxShadow: "none",
                      position: "relative",
                      zIndex: "0",
                      "&:hover": { borderRadius: "0.65rem", transform: "scale(1.08)", zIndex: 3 },
                      display: "flex",
                      backgroundColor: "#fff",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: "1.5rem",
                    }}
                    >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      <div>
                        <Box sx={[{ fontWeight: 700 }, {
                          fontSize: "1.15rem !important",
                          color: "#070919",
                          "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
                        }]}>
                          {/* Cash on Cash Return */}
                          Leveraged Return
                        </Box>
                        <Box sx={[{ display: 'flex', alignItems: 'center', }, { fontSize: "1rem !important" }]}>
                          <Box sx={{
                            color: "#070919",
                            "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
                          }}>
                            Cash on Cash return for portfolio.
                          </Box>
                        </Box>
                      </div>
                    </Box>
                    <div>
                      <Box component="span" sx={{ pr: '0.25rem' }}>
                        <CountUp
                          start={0}
                          end={countUpSafePercent(cashOnCash * 100)}
                          duration={1}
                          separator=","
                          delay={1}
                          decimals={2}
                          decimal="."
                          prefix=""
                          suffix="%"
                          style={{ fontSize: 24 }}
                        />
                      </Box>
                    </div>
                  </ListItemButton>
                  <ListItemButton
                    component="a"
                    href={null}
                    onClick={() => toggle1('Net Cashflow')}
                    sx={{
                      transition: "all 0.2s ease-in-out !important",
                      transform: "scale(1) !important",
                      boxShadow: "none",
                      position: "relative",
                      zIndex: "0",
                      "&:hover": { borderRadius: "0.65rem", transform: "scale(1.08)", zIndex: 3 },
                      display: "flex",
                      backgroundColor: "#fff",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: "1.5rem",
                    }}
                    >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      <div>
                        <Box sx={[{ fontWeight: 700 }, {
                          fontSize: "1.15rem !important",
                          color: "#070919",
                          "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
                        }]}>
                          Net Annual Cashflow
                        </Box>
                        <Box sx={[{ display: 'flex', alignItems: 'center', }, { fontSize: "1rem !important" }]}>
                          <Box sx={{
                            color: "#070919",
                            "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
                          }}>
                            Net annual cashflow after all expenses are
                            paid.
                          </Box>
                        </Box>
                      </div>
                    </Box>
                    <div>
                      <Box component="span" sx={{ pr: '0.25rem' }}>
                        <CountUp
                          start={0}
                          end={cashFlow}
                          duration={1}
                          separator=","
                          delay={1}
                          decimals={0}
                          decimal="."
                          prefix="$"
                          suffix=""
                          style={{ fontSize: 24 }}
                        />
                      </Box>
                    </div>
                  </ListItemButton>
                </List>
              )}
              {activeTab === '1' && (
                <List component="div" sx={[{
                  paddingTop: 0,
                  paddingBottom: 0,
                  "& .MuiListItem-root, & .MuiListItemButton-root": {
                    width: "100%",
                    padding: "0.75rem 1.25rem",
                    backgroundColor: "#fff",
                    border: "1px solid rgba(0, 0, 0, 0.125)",
                    borderRadius: 0,
                    margin: 0,
                    borderWidth: "0 0 1px",
                    "&.MuiListItem-button": {
                      borderRadius: 0,
                      borderWidth: "0 0 1px",
                      "& .MuiTouchRipple-root": { opacity: 0.2 },
                    },
                    "&.list-group-item-action": { transition: "all 0.2s ease-in-out" },
                    "&.MuiListItem-button:hover, &.list-group-item-action:hover": { background: "#f4f5fd", zIndex: 1 },
                    "&.MuiListItem-button:active, &.list-group-item-action:active": { color: "#000", backgroundColor: "#eeeff8" },
                    "&:first-of-type": { borderTop: "0 !important" },
                    "&:last-child": { borderBottom: "0 !important" },
                  },
                }, { pb: 1 }]}>
                  {/* <ListItemButton
                    component="a"
                    href={null}
                    //onClick={(e) => e.preventDefault()}>
                    <div>
                      <div>
                        <div>
                          Total Value
                        </div>
                        <div>
                          <div>Total value of portfolio if sold today.</div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <span>
                        <CountUp
                          start={0}
                          end={totalValue}
                          duration={1}
                          separator=","
                          delay={1}
                          decimals={0}
                          decimal="."
                          prefix="$"
                          suffix=""
                          style={{ fontSize: 24 }}
                        />
                      </span>
                    </div>
                  </ListItemButton>
                  <ListItemButton
                    component="a"
                    href={null}
                    onClick={(e) => e.preventDefault()}>
                    <div>
                      <div>
                        <div>
                          Equity
                        </div>
                        <div>
                          <div>Sum of equity for all properties in portfolio.</div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <span>
                        <CountUp
                          start={0}
                          end={equity}
                          duration={1}
                          separator=","
                          delay={1}
                          decimals={0}
                          decimal="."
                          prefix="$"
                          suffix=""
                          style={{ fontSize: 24 }}
                        />
                      </span>
                    </div>
                  </ListItemButton> */}
                  <ListItemButton
                    component="a"
                    href={null}
                    onClick={() => toggle1('Net ROI')}
                    sx={{
                      transition: "all 0.2s ease-in-out !important",
                      transform: "scale(1) !important",
                      boxShadow: "none",
                      position: "relative",
                      zIndex: "0",
                      "&:hover": { borderRadius: "0.65rem", transform: "scale(1.08)", zIndex: 3 },
                      display: "flex",
                      backgroundColor: "#fff",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: "1.5rem",
                    }}
                    >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      <div>
                        <Box sx={[{ fontWeight: 700 }, {
                          fontSize: "1.15rem !important",
                          color: "#070919",
                          "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
                        }]}>
                          Net ROI
                        </Box>
                        <Box sx={[{ display: 'flex', alignItems: 'center', }, { fontSize: "1rem !important" }]}>
                          <Box sx={{
                            color: "#070919",
                            "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
                          }}>
                            Total value of portfolio if sold today.
                          </Box>
                        </Box>
                      </div>
                    </Box>
                    <div>
                      <Box component="span" sx={{ pr: '0.25rem' }}>
                        <CountUp
                          start={0}
                          end={countUpSafePercent(netROI * 100)}
                          duration={1}
                          separator=","
                          delay={1}
                          decimals={2}
                          decimal="."
                          prefix=""
                          suffix="%"
                          style={{ fontSize: 24 }}
                        />
                      </Box>
                    </div>
                  </ListItemButton>
                  <ListItemButton
                    component="a"
                    href={null}
                    onClick={() => toggle1('Net Profit')}
                    sx={{
                      transition: "all 0.2s ease-in-out !important",
                      transform: "scale(1) !important",
                      boxShadow: "none",
                      position: "relative",
                      zIndex: "0",
                      "&:hover": { borderRadius: "0.65rem", transform: "scale(1.08)", zIndex: 3 },
                      display: "flex",
                      backgroundColor: "#fff",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: "1.5rem",
                    }}
                    >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      <div>
                        <Box sx={[{ fontWeight: 700 }, {
                          fontSize: "1.15rem !important",
                          color: "#070919",
                          "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
                        }]}>
                          Net Profit
                        </Box>
                        <Box sx={[{ display: 'flex', alignItems: 'center', }, { fontSize: "1rem !important" }]}>
                          <Box sx={{
                            color: "#070919",
                            "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
                          }}>
                            Your return after the expenses associated
                            with the sale.
                          </Box>
                        </Box>
                      </div>
                    </Box>
                    <div>
                      <Box component="span" sx={{ pr: '0.25rem' }}>
                        <CountUp
                          start={0}
                          end={proceedsAfterSale}
                          duration={1}
                          separator=","
                          delay={1}
                          decimals={0}
                          decimal="."
                          prefix="$"
                          suffix=""
                          style={{ fontSize: 24 }}
                        />
                      </Box>
                    </div>
                  </ListItemButton>
                </List>
              )}
              {activeTab === '2' && (
                <List component="div" sx={[{
                  paddingTop: 0,
                  paddingBottom: 0,
                  "& .MuiListItem-root, & .MuiListItemButton-root": {
                    width: "100%",
                    padding: "0.75rem 1.25rem",
                    backgroundColor: "#fff",
                    border: "1px solid rgba(0, 0, 0, 0.125)",
                    borderRadius: 0,
                    margin: 0,
                    borderWidth: "0 0 1px",
                    "&.MuiListItem-button": {
                      borderRadius: 0,
                      borderWidth: "0 0 1px",
                      "& .MuiTouchRipple-root": { opacity: 0.2 },
                    },
                    "&.list-group-item-action": { transition: "all 0.2s ease-in-out" },
                    "&.MuiListItem-button:hover, &.list-group-item-action:hover": { background: "#f4f5fd", zIndex: 1 },
                    "&.MuiListItem-button:active, &.list-group-item-action:active": { color: "#000", backgroundColor: "#eeeff8" },
                    "&:first-of-type": { borderTop: "0 !important" },
                    "&:last-child": { borderBottom: "0 !important" },
                  },
                }, { pb: 1 }]}>
                  <ListItemButton
                    component="a"
                    href={null}
                    onClick={(e) => e.preventDefault()}
                    sx={{
                      transition: "all 0.2s ease-in-out !important",
                      transform: "scale(1) !important",
                      boxShadow: "none",
                      position: "relative",
                      zIndex: "0",
                      "&:hover": { borderRadius: "0.65rem", transform: "scale(1.08)", zIndex: 3 },
                      display: "flex",
                      backgroundColor: "#fff",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: "1.5rem",
                    }}
                    >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      <div>
                        <Box sx={[{ fontWeight: 700 }, {
                          fontSize: "1.15rem !important",
                          color: "#070919",
                          "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
                        }]}>
                          Gross Income
                        </Box>
                      </div>
                    </Box>
                    <div>
                      <Box component="span" sx={{ pr: '0.25rem' }}>
                        <CountUp
                          start={0}
                          end={countUpSafePercent(IRR * 100)}
                          duration={1}
                          separator=","
                          delay={1}
                          decimals={2}
                          decimal="."
                          prefix="$"
                          suffix=""
                          style={{ fontSize: 24 }}
                        />
                      </Box>
                    </div>
                  </ListItemButton>
                  <ListItemButton
                    component="a"
                    href={null}
                    onClick={(e) => e.preventDefault()}
                    sx={{
                      transition: "all 0.2s ease-in-out !important",
                      transform: "scale(1) !important",
                      boxShadow: "none",
                      position: "relative",
                      zIndex: "0",
                      "&:hover": { borderRadius: "0.65rem", transform: "scale(1.08)", zIndex: 3 },
                      display: "flex",
                      backgroundColor: "#fff",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: "1.5rem",
                    }}
                    >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      <div>
                        <Box sx={[{ fontWeight: 700 }, {
                          fontSize: "1.15rem !important",
                          color: "#070919",
                          "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
                        }]}>
                          Net Income
                        </Box>
                      </div>
                    </Box>
                    <div>
                      <Box component="span" sx={{ pr: '0.25rem' }}>
                        <CountUp
                          start={0}
                          end={countUpSafePercent(IRR * 100)}
                          duration={1}
                          separator=","
                          delay={1}
                          decimals={2}
                          decimal="."
                          prefix="$"
                          suffix=""
                          style={{ fontSize: 24 }}
                        />
                      </Box>
                    </div>
                  </ListItemButton>
                  <ListItemButton
                    component="a"
                    href={null}
                    onClick={(e) => e.preventDefault()}
                    sx={{
                      transition: "all 0.2s ease-in-out !important",
                      transform: "scale(1) !important",
                      boxShadow: "none",
                      position: "relative",
                      zIndex: "0",
                      "&:hover": { borderRadius: "0.65rem", transform: "scale(1.08)", zIndex: 3 },
                      display: "flex",
                      backgroundColor: "#fff",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: "1.5rem",
                    }}
                    >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      <div>
                        <Box sx={[{ fontWeight: 700 }, {
                          fontSize: "1.15rem !important",
                          color: "#070919",
                          "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
                        }]}>
                          Expense Ratio
                        </Box>
                      </div>
                    </Box>
                    <div>
                      <Box component="span" sx={{ pr: '0.25rem' }}>
                        <CountUp
                          start={0}
                          end={countUpSafePercent(IRR * 100)}
                          duration={1}
                          separator=","
                          delay={1}
                          decimals={2}
                          decimal="."
                          prefix=""
                          suffix="%"
                          style={{ fontSize: 24 }}
                        />
                      </Box>
                    </div>
                  </ListItemButton>
                  <ListItemButton
                    component="a"
                    href={null}
                    onClick={(e) => e.preventDefault()}
                    sx={{
                      transition: "all 0.2s ease-in-out !important",
                      transform: "scale(1) !important",
                      boxShadow: "none",
                      position: "relative",
                      zIndex: "0",
                      "&:hover": { borderRadius: "0.65rem", transform: "scale(1.08)", zIndex: 3 },
                      display: "flex",
                      backgroundColor: "#fff",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: "1.5rem",
                    }}
                    >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      <div>
                        <Box sx={[{ fontWeight: 700 }, {
                          fontSize: "1.15rem !important",
                          color: "#070919",
                          "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
                        }]}>
                          Avg. YTD Collection Rate
                        </Box>
                        <Box sx={[{ display: 'flex', alignItems: 'center', }, { fontSize: "1rem !important" }]}>
                          <Box sx={{
                            color: "#070919",
                            "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
                          }}>
                            Sum of total costs associated with
                            properties.
                          </Box>
                        </Box>
                      </div>
                    </Box>
                    <div>
                      <Box component="span" sx={{ pr: '0.25rem' }}>
                        <CountUp
                          start={0}
                          end={90}
                          duration={1}
                          separator=","
                          delay={1}
                          decimals={2}
                          decimal="."
                          prefix=""
                          suffix="%"
                          style={{ fontSize: 24 }}
                        />
                      </Box>
                    </div>
                  </ListItemButton>
                </List>
              )}
            </Card>
          </Grid>
        </Grid>
      </Box>
      {map(analytics, (section) => (
        <Dialog
          key={section.label}
          open={modal1 === section.label}
          onClose={toggle1Close}
          maxWidth="lg"
          scroll="body"
          slotProps={{ paper: { sx: calcDialogPaperSx } }}
        >
          <Grid container spacing={0}>
            <Grid size={5}>
              <Box sx={[{ height: '100%', borderRadius: '0.65rem' }, {
                position: "relative",
                display: "flex",
                flexDirection: "column",
                "& > .divider": { zIndex: 7, width: "100%" },
                "& .hero-footer": { zIndex: 7 },
                "& .header-nav-wrapper": { zIndex: 27 },
                "& .bg-composed-wrapper--image": { backgroundRepeat: "no-repeat", backgroundSize: "cover", backgroundPosition: "center center" },
                borderTopLeftRadius: "inherit",
                borderTopRightRadius: "inherit",
                alignItems: "center",
                ".MuiCard-root > &": {
                  top: -1,
                  marginLeft: -1,
                  marginRight: -1,
                  marginBottom: -2,
                  zIndex: 6,
                },
                backgroundImage: "linear-gradient(135deg, var(--hf-calc-hero-from, #CE9FFC) 10%, var(--hf-calc-hero-to, #7367F0) 100%) !important",
                  "html[data-color-mode=\"dark\"] & .MuiTypography-root": { color: "rgba(255, 255, 255, 0.92) !important" },
              }, {
                "@media (min-width: 1440px)": { borderTopRightRadius: "0 !important", borderBottomRightRadius: "0 !important" },
              }]}>
                <Box
                  sx={{
                    flexGrow: 1,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Box sx={[{ borderRadius: '0.65rem' }, {
                    position: "absolute",
                    left: 0,
                    top: 0,
                    height: "100%",
                    width: "100%",
                    zIndex: 5,
                    opacity: "0.7 !important",
                  }, {
                    "@media (min-width: 1440px)": { borderTopRightRadius: "0 !important", borderBottomRightRadius: "0 !important" },
                  }]} style={{ backgroundColor: '#070919' }} />
                  <Box sx={[{ justifyContent: 'center', textAlign: 'center', p: '3rem', '@media (min-width: 1440px)': { justifyContent: 'flex-start', textAlign: 'left' }, }, { position: "relative", zIndex: "7", width: "100%" }]}>
                    <Box sx={{ color: '#fff', mt: '1rem' }}>
                      <Typography
                        style={{ fontSize: 30, fontWeight: 600 }}
                      >
                        Calculation
                      </Typography>
                    </Box>
                    {section.calculation}
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid size={7}>
              <div>
                <Card>
                  <CardHeader title={section.label} />
                  <Divider />
                  <CardContent style={{ height: '100%' }}>
                    <Grid
                      container
                      direction="row"
                      sx={{
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Grid size={12} style={{ marginBottom: 10 }}>
                        <Card
                          style={{
                            backgroundColor: '#eff2f5',
                            borderRadius: 0,
                          }}
                        >
                          <CardContent>
                            <Typography>
                              {section.description}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </CardContent>
                  <Divider />
                  <CardActions>
                    <Grid
                      container
                      sx={{
                        justifyContent: 'center',
                      }}
                    >
                      <Grid>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={toggle1Close}
                        >
                          Close
                        </Button>
                      </Grid>
                    </Grid>
                  </CardActions>
                </Card>
              </div>
            </Grid>
          </Grid>
        </Dialog>
      ))}
    </>
  )
}
